import React, { useEffect, useRef } from 'react';
import SEO from '../components/SEO';
import { seoData } from '../utils/seoData';

const IndustriesPage = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const industries = [
    {
      title: "Cook wares",
      description: "At present cookware serves as our largest consumer base. We ourselves manufacture around 25tonnes of cookware a month and supply circles/blanks and sheets to cookware maker all over the nation.",
      icon: "fas fa-utensils",
      image: "/images/61IrhEZ4Y5L.jpg",
      videoSrc: "/videos/bb-cookware-applications.mp4",
      applications: [
        "All kinds of utensils with varied finishes",
        "Non-stick cookware", 
        "Hard anodized utensils",
        "Raw mill finish products",
        "Sandwich bottom stainless steel utensils",
        "Induction base aluminium utensils"
      ],
      benefits: [
        "Circles/sheets for nationwide supply",
        "Multiple finish options available",
        "25 tonnes monthly production capacity",
        "Complete manufacturing solution"
      ]
    },
    {
      title: "Lighting",
      description: "Our circles/sheets can be used to make reflectors using spinning, cnc machining and forging. The alloys that are used for it are mainly 1040, 1050 & 1070.",
      icon: "fas fa-lightbulb",
      image: "/images/Circles/aluminium-reflector-1000x1000.jpg", 
      videoSrc: "/videos/bb-lighting-applications.mp4",
      applications: [
        "Reflectors using spinning",
        "CNC machined reflectors",
        "Forged reflector components",
        "Various shaped reflectors"
      ],
      benefits: [
        "Highly ductile alloys (1040, 1050, 1070)",
        "Powder coating finish options",
        "Anodizing capabilities", 
        "Mirror polish finish available"
      ]
    },
    {
      title: "Large Cans",
      description: "The circles of grades 1070 'O' Temper is used for the specific purpose. As aluminium is mostly harmless to human body and as a metal free from endocrine disruptors it is very suitable to make large cans.",
      icon: "fas fa-box",
      image: "/images/aluminium-milk-cans-1000x1000.webp",
      videoSrc: "/videos/bb-large-cans-applications.mp4", 
      applications: [
        "Storage of chemicals",
        "Medicinal raw materials storage",
        "Large capacity containers",
        "Industrial storage solutions"
      ],
      benefits: [
        "Grade 1070 'O' Temper circles",
        "Harmless to human body",
        "Free from endocrine disruptors",
        "Safe for chemical storage"
      ]
    },
    {
      title: "Downstream Industries",
      description: "The is a sector where in our trading part of business gets involved. Aluminium downstream industries are dependent on aluminium scraps like extrusions, UBC, Talon, Turnings etc.",
      icon: "fas fa-industry",
      image: "/images/scraps/aluminium-extrusion-scrap.jpg",
      videoSrc: "/videos/bb-downstream-applications.mp4",
      applications: [
        "Profiles manufacturing",
        "Cables production", 
        "Notch bar for steel making",
        "Various finished goods"
      ],
      benefits: [
        "Raw material conversion expertise",
        "Extrusions, UBC, Talon, Turnings supply",
        "Reducing agent for steel making",
        "Complete trading solutions"
      ]
    }
  ];

  return (
    <div className="industries-page">
      <SEO 
        title={seoData.industries.title}
        description={seoData.industries.description}
        keywords={seoData.industries.keywords}
        canonical={seoData.industries.canonical}
        ogImage={seoData.industries.ogImage}
      />
      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .industries-page {
          font-family: 'Inter', 'Poppins', sans-serif;
          line-height: 1.6;
          color: var(--graphite, #1E2329);
          overflow-x: hidden;
        }

        /* Hero Section */
        .hero {
          background: linear-gradient(135deg, #5A1FB3 0%, #1E2329 100%);
          padding: 120px 0 80px;
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="%23ffffff" opacity="0.05"><polygon points="0,0 1000,0 1000,100 0,80"/></svg>');
          background-size: cover;
        }

        .hero-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          text-align: center;
          position: relative;
          z-index: 2;
        }

        .hero h1 {
          font-size: 3.5rem;
          font-weight: 800;
          color: white;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1.3rem;
          color: rgba(255, 255, 255, 0.9);
          max-width: 800px;
          margin: 0 auto 2rem;
          font-weight: 400;
        }

        .hero-icon {
          width: 80px;
          height: 80px;
          background: #7B3FE4;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          box-shadow: 0 10px 30px rgba(123, 63, 228, 0.3);
        }

        .hero-icon i {
          font-size: 2rem;
          color: white;
        }

        /* Container */
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-header h2 {
          font-size: 2.8rem;
          font-weight: 700;
          background: linear-gradient(135deg, var(--brand-purple, #7B3FE4) 0%, var(--deep-plum, #5A1FB3) 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 1rem;
        }

        .section-header p {
          font-size: 1.2rem;
          color: var(--slate-grey, #4A5260);
          max-width: 600px;
          margin: 0 auto;
        }

        /* Industries Grid */
        .industries-section {
          padding: 100px 0;
          background: linear-gradient(135deg, var(--off-white, #F6F7F9) 0%, var(--metallic-silver, #C9CDD3) 100%);
        }

        .industries-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 4rem;
        }

        .industry-card {
          background: white;
          border-radius: 25px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
          transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          position: relative;
        }

        .industry-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 5px;
          background: linear-gradient(90deg, var(--brand-purple, #7B3FE4), var(--electric-violet, #9A60FF));
        }

        .industry-card:hover {
          transform: translateY(-15px);
          box-shadow: 0 35px 90px rgba(0, 0, 0, 0.15);
        }

        .industry-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
          min-height: 500px;
        }

        .industry-media {
          position: relative;
          overflow: hidden;
        }

        .industry-image-placeholder {
          width: 100%;
          height: 100%;
          background: #F7FAFC;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px dashed #7B3FE4;
          transition: all 0.4s ease;
        }

        .industry-card:hover .industry-image-placeholder {
          background: rgba(123, 63, 228, 0.05);
          border-color: #9A60FF;
        }

        .industry-placeholder-content {
          text-align: center;
          color: #7B3FE4;
        }

        .industry-placeholder-content i {
          font-size: 4rem;
          margin-bottom: 1.5rem;
          display: block;
        }

        .industry-placeholder-text {
          font-size: 1.3rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .industry-placeholder-desc {
          font-size: 1rem;
          opacity: 0.8;
          max-width: 250px;
        }

        .media-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(123, 63, 228, 0.1), rgba(90, 31, 179, 0.1));
          opacity: 0;
          transition: opacity 0.4s ease;
        }

        .industry-card:hover .media-overlay {
          opacity: 1;
        }

        .industry-content {
          padding: 3rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .industry-header {
          display: flex;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .industry-icon {
          width: 70px;
          height: 70px;
          background: linear-gradient(135deg, var(--brand-purple, #7B3FE4), var(--electric-violet, #9A60FF));
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1.5rem;
          transition: all 0.4s ease;
        }

        .industry-card:hover .industry-icon {
          transform: scale(1.1) rotate(8deg);
          box-shadow: 0 10px 25px rgba(123, 63, 228, 0.3);
        }

        .industry-icon i {
          font-size: 2rem;
          color: white;
        }

        .industry-card h3 {
          font-size: 2rem;
          font-weight: 700;
          color: var(--graphite, #1E2329);
          line-height: 1.3;
        }

        .industry-description {
          color: var(--slate-grey, #4A5260);
          font-size: 1.1rem;
          line-height: 1.7;
          margin-bottom: 2rem;
        }

        .industry-details {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
        }

        .detail-section h4 {
          font-size: 1.2rem;
          font-weight: 600;
          color: var(--brand-purple, #7B3FE4);
          margin-bottom: 1rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-list {
          list-style: none;
        }

        .detail-list li {
          padding: 0.5rem 0;
          color: var(--slate-grey, #4A5260);
          position: relative;
          padding-left: 1.5rem;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .detail-list li::before {
          content: '▶';
          position: absolute;
          left: 0;
          color: var(--brand-purple, #7B3FE4);
          font-size: 0.8rem;
          top: 50%;
          transform: translateY(-50%);
        }

        .detail-list li:hover {
          color: var(--brand-purple, #7B3FE4);
          transform: translateX(5px);
        }

        /* Reverse layout for even cards */
        .industry-card:nth-child(even) .industry-layout {
          grid-template-columns: 1fr 1fr;
        }

        .industry-card:nth-child(even) .industry-media {
          order: 2;
        }

        .industry-card:nth-child(even) .industry-content {
          order: 1;
        }

        /* Statistics Section */
        .stats-section {
          padding: 100px 0;
          background: linear-gradient(135deg, var(--graphite, #1E2329) 0%, var(--slate-grey, #4A5260) 100%);
          color: white;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2rem;
        }

        .stat-card {
          text-align: center;
          padding: 2rem;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 15px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          background: rgba(255, 255, 255, 0.08);
          transform: translateY(-5px);
        }

        .stat-number {
          font-size: 3rem;
          font-weight: 800;
          background: linear-gradient(135deg, var(--brand-purple, #7B3FE4), var(--electric-violet, #9A60FF));
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          display: block;
          margin-bottom: 0.5rem;
        }

        .stat-label {
          font-size: 1.1rem;
          font-weight: 500;
          opacity: 0.9;
        }

        /* CTA Section */
        .cta-section {
          padding: 100px 0;
          background: linear-gradient(135deg, var(--brand-purple, #7B3FE4) 0%, var(--deep-plum, #5A1FB3) 100%);
          text-align: center;
          color: white;
        }

        .cta-content {
          max-width: 800px;
          margin: 0 auto;
        }

        .cta-content h2 {
          font-size: 2.8rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
        }

        .cta-content p {
          font-size: 1.3rem;
          margin-bottom: 2.5rem;
          opacity: 0.9;
        }

        .cta-button {
          display: inline-block;
          padding: 18px 40px;
          background: white;
          color: var(--brand-purple, #7B3FE4);
          text-decoration: none;
          border-radius: 50px;
          font-weight: 700;
          font-size: 1.1rem;
          transition: all 0.4s ease;
          box-shadow: 0 10px 30px rgba(255, 255, 255, 0.2);
        }

        .cta-button:hover {
          transform: translateY(-3px) scale(1.05);
          box-shadow: 0 15px 40px rgba(255, 255, 255, 0.3);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .hero h1 {
            font-size: 2.5rem;
          }

          .hero-subtitle {
            font-size: 1.1rem;
          }

          .section-header h2 {
            font-size: 2.2rem;
          }

          .container {
            padding: 0 1rem;
          }

          .industry-layout {
            grid-template-columns: 1fr;
          }

          .industry-card:nth-child(even) .industry-layout {
            grid-template-columns: 1fr;
          }

          .industry-card:nth-child(even) .industry-media,
          .industry-card:nth-child(even) .industry-content {
            order: unset;
          }

          .industry-content {
            padding: 2rem;
          }

          .industry-details {
            grid-template-columns: 1fr;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fadeInUp 0.8s ease-out;
        }
      `}</style>

      {/* Add Font Awesome and Google Fonts */}
      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* Hero Section */}
      <section className="hero">

        <div className="hero-content">
          <div className="hero-icon animate-fade-in">
            <i className="fas fa-industry"></i>
          </div>
          <h1 className="animate-fade-in">Industries We Serve</h1>
          <p className="hero-subtitle animate-fade-in">
            Aluminium being a metal of great usage and future has tons of use case. From aerospace, appliances, conductors, cookwares, EVs and many more. As we are involved in the complete cycle of aluminium right from recycling to consumer goods, we have the ability to add value in each step.
          </p>
        </div>
      </section>

      {/* Industries Section */}
      <section className="industries-section">
        <div className="container">
          <div className="section-header">
            <h2>Industries We Serve</h2>
            <p>As a circles/blanks/sheet producer, we supply other industries with their raw materials. As a utensils manufacturer reach out the end consumer. As a future reflector manufacturer we will be able to supply lighting manufacturer with their intermediate product. As scrap dealer providing industries with raw material. We are involved in each.</p>
          </div>
          
          <div className="industries-grid">
            {industries.map((industry, index) => (
              <div key={index} className="industry-card animate-on-scroll">
                <div className="industry-layout">
                  <div className="industry-media">
                    {industry.image ? (
                      <img
                        src={industry.image}
                        alt={`${industry.title} - BB Industries`}
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div className="industry-image-placeholder">
                        <div className="industry-placeholder-content">
                          <i className={industry.icon}></i>
                          <div className="industry-placeholder-text">{industry.title}</div>
                          <div className="industry-placeholder-desc">Industry Applications</div>
                        </div>
                      </div>
                    )}
                    <div className="media-overlay"></div>
                  </div>
                  <div className="industry-content">
                    <div className="industry-header">
                      <div className="industry-icon">
                        <i className={industry.icon}></i>
                      </div>
                      <h3>{industry.title}</h3>
                    </div>
                    
                    <p className="industry-description">{industry.description}</p>
                    
                    <div className="industry-details">
                      <div className="detail-section">
                        <h4>Applications</h4>
                        <ul className="detail-list">
                          {industry.applications.map((app, i) => (
                            <li key={i}>{app}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="detail-section">
                        <h4>Key Benefits</h4>
                        <ul className="detail-list">
                          {industry.benefits.map((benefit, i) => (
                            <li key={i}>{benefit}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Partner with Industry Leaders</h2>
            <p>Ready to explore how our aluminum solutions can enhance your industry applications? Connect with our technical team for specialized consultation.</p>
            <a href="/contact" className="cta-button">
              Get Industry Solutions
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IndustriesPage;